/**
 * projectsStore — UI-only client state for the projects feature.
 *
 * The server list (Project[]) lives in TanStack Query so cache invalidation,
 * background refetch and request dedup come for free (see
 * ``hooks/useProjects.ts``). Zustand owns ONLY the user's per-device
 * selection — which project the ProjectPill shows + which one new
 * conversations default to.
 *
 * Persisted via ``zustandStorage`` (localStorage on web, swappable on
 * desktop). Key bumps on schema break: ``ando.projects.v1``.
 *
 * Design note: keeping the server collection out of Zustand avoids the
 * "two sources of truth" smell that plagued the v1 ConversationContext
 * (the 1576 LoC monolith called out in REUSE_FROM_V1.md). TanStack Query
 * stays the single owner of every server collection.
 */

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { zustandStorage } from "@/lib/storage";

interface ProjectsState {
  /**
   * Last selected project id. ``null`` → no explicit selection yet; the
   * resolver in ``useCurrentProject`` falls back to the first available
   * project from the server list.
   */
  currentProjectId: string | null;
  setCurrentProjectId: (id: string | null) => void;
}

const STORAGE_KEY = "ando.projects.v1";

export const useProjectsStore = create<ProjectsState>()(
  persist(
    (set) => ({
      currentProjectId: null,
      setCurrentProjectId: (id) => set({ currentProjectId: id }),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => zustandStorage),
      // Only persist the selected id — the rest is derivable from the
      // server list and would race the TanStack hydrate on boot.
      partialize: (state) => ({ currentProjectId: state.currentProjectId }),
    },
  ),
);

// ── Type-safe selectors (subscribe to slices to avoid re-renders) ─────────

export const selectCurrentProjectId = (state: ProjectsState): string | null =>
  state.currentProjectId;

export const selectSetCurrentProjectId = (
  state: ProjectsState,
): ProjectsState["setCurrentProjectId"] => state.setCurrentProjectId;
